import { ECurrency } from '@ringana/configs';
import { ITrackingProductData } from '@/common/interfaces/tracking.interface';
import { IShoppingCartIndividual, IShoppingCartItem } from '@/common/interfaces/cart.interface';
import FormatHelper from '@/helpers/FormatHelper';
import TransformHelper from '@/helpers/TransformHelper';

// Matomo uses _paq on the window, we can't get rid of the underscore there
/* eslint-disable no-underscore-dangle */
export default class TrackingHelper {
  public static trackPageView(): void {
    if(window._paq) {
      window._paq.push([`trackPageView`]);
    }
  }

  public static trackProductView(matchcode: string, productName: string, categoryName: string, price: number): void {
    const euroPrice = FormatHelper.getEuroPrice(price);

    if(window._paq && euroPrice >= 0) {
      window._paq.push([
        `setEcommerceView`,
        matchcode,
        productName,
        categoryName,
        euroPrice,
      ]);
    }

    if(window.pintrk && euroPrice >= 0) {
      window.pintrk(`track`, `pagevisit`, {
        line_items: [{
          product_category: categoryName,
          product_id: matchcode,
          product_name: productName,
          product_price: euroPrice,
        }],
      });
    }

    this.trackPageView();
  }

  public static trackCartTotal(total: number): void {
    const euroTotal = FormatHelper.getEuroPrice(total);

    if(window._paq && euroTotal >= 0) {
      window._paq.push([
        `trackEcommerceCartUpdate`,
        euroTotal,
      ]);
    }
  }

  public static trackCartAddItem(matchcode: string, productName: string, categoryName: string, price: number, amount: number, total: number): void {
    const euroPrice = FormatHelper.getEuroPrice(price);

    if(euroPrice < 0 || total < 0) {
      return;
    }

    if(window._paq) {
      window._paq.push([
        `addEcommerceItem`,
        matchcode,
        productName,
        categoryName,
        euroPrice,
        amount,
      ]);
    }

    if(window.pintrk) {
      window.pintrk(`track`, `addtocart`, {
        currency: ECurrency.EUR.toUpperCase(),
        line_items: [{
          product_category: categoryName,
          product_id: matchcode,
          product_name: productName,
          product_price: euroPrice,
          product_quantity: amount,
        }],
        order_quantity: amount,
        value: parseFloat((amount * euroPrice).toFixed(2)),
      });
    }

    this.trackCartTotal(total);
  }

  public static trackCartRemoveItem(matchcode: string, total: number): void {
    if(window._paq && total >= 0) {
      window._paq.push([
        `removeEcommerceItem`,
        matchcode,
      ]);

      this.trackCartTotal(total);
    }
  }

  public static trackOrder(orderId: string, shoppingCarts: IShoppingCartIndividual[]): void {
    const { euroTotal, products } = this.calculateProductsAndTotal(shoppingCarts);

    if(euroTotal < 0 || products.filter(product => product.price < 0).length > 0) {
      return;
    }

    if(window._paq) {
      window._paq.push([
        `trackEcommerceOrder`,
        orderId,
        euroTotal,
      ]);
    }

    if(window.pintrk) {
      window.pintrk(`track`, `checkout`, {
        currency: ECurrency.EUR.toUpperCase(),
        line_items: products.map(product => TransformHelper.transformTrackingProductDataToPinterestProductData(product)),
        order_id: orderId,
        value: euroTotal,
      });
    }
  }

  public static trackSignUp(): void {
    if(window.pintrk) {
      window.pintrk(`track`, `signup`);
    }
  }

  public static trackNewsletterRegistration(): void {
    if(window.pintrk) {
      window.pintrk(`track`, `lead`, {
        lead_type: `Newsletter`,
      });
    }
  }

  public static trackSearch(searchString: string): void {
    if(window.pintrk) {
      window.pintrk(`track`, `search`, {
        search_query: searchString,
      });
    }
  }

  public static calculateProductsAndTotal(shoppingCarts: IShoppingCartIndividual[]): { euroTotal: number; products: ITrackingProductData[] } {
    const items: IShoppingCartItem[] = [];
    let total = 0;

    for(const shoppingCart of shoppingCarts) {
      total += shoppingCart.sum.total;
      for(const shoppingCartItem of shoppingCart.items) {
        items.push(shoppingCartItem);
      }
    }

    const products = TransformHelper.transformShoppingCartItemsToTrackingProductData(items);
    for(const product of products) {
      product.price = FormatHelper.getEuroPrice(product.price);
    }

    return {
      euroTotal: FormatHelper.getEuroPrice(total),
      products,
    };
  }
}
